import Papa from 'papaparse'

export interface ParsedData {
  headers: string[]
  rows: Record<string, any>[]
  errors: string[]
}

export function parseCSV(csvString: string): Promise<ParsedData> {
  return new Promise((resolve) => {
    Papa.parse(csvString, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header: string) => header.trim(),
      complete: (results) => {
        const errors: string[] = []
        
        // Collect parsing errors
        if (results.errors.length > 0) {
          errors.push(...results.errors.map(err => `Row ${err.row}: ${err.message}`))
        }

        // Get headers
        const headers = results.meta.fields || []
        
        // Clean and validate data
        const rows = results.data.map((row: any, index: number) => {
          const cleanRow: Record<string, any> = {}
          
          headers.forEach(header => {
            let value = row[header]
            
            // Clean the value
            if (typeof value === 'string') {
              value = value.trim()
            }
            
            // Try to parse numbers
            if (typeof value === 'string' && value !== '') {
              const numValue = parseFloat(value)
              if (!isNaN(numValue) && isFinite(numValue)) {
                value = numValue
              }
            }
            
            cleanRow[header] = value
          })
          
          return cleanRow
        })

        resolve({
          headers,
          rows,
          errors
        })
      }
    })
  })
}

export function parsePastedData(pastedText: string): ParsedData {
  const lines = pastedText.split('\n').filter(line => line.trim())
  
  if (lines.length === 0) {
    return { headers: [], rows: [], errors: ['No data found'] }
  }

  // Check if it looks like CSV (contains commas or tabs)
  const firstLine = lines[0]
  const separator = firstLine.includes('\t') ? '\t' : 
                   firstLine.includes(',') ? ',' : 
                   firstLine.includes(';') ? ';' : null

  if (!separator) {
    return { headers: [], rows: [], errors: ['Could not detect data format'] }
  }

  // Parse headers
  const headers = firstLine.split(separator).map(h => h.trim().replace(/^"|"$/g, ''))
  
  // Parse rows
  const rows: Record<string, any>[] = []
  const errors: string[] = []

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i]
    const values = line.split(separator).map(v => v.trim().replace(/^"|"$/g, ''))
    
    if (values.length !== headers.length) {
      errors.push(`Row ${i}: Expected ${headers.length} columns, got ${values.length}`)
      continue
    }

    const row: Record<string, any> = {}
    headers.forEach((header, index) => {
      let value: any = values[index]
      
      // Try to parse numbers
      if (typeof value === 'string' && value !== '') {
        const numValue = parseFloat(value)
        if (!isNaN(numValue) && isFinite(numValue)) {
          value = numValue
        }
      }
      
      row[header] = value
    })
    
    rows.push(row)
  }

  return { headers, rows, errors }
}

export function generateColumnDefinitions(headers: string[]) {
  return headers.map(header => ({
    accessorKey: header,
    header: header,
    enableSorting: true,
    enableColumnFilter: true,
  }))
}